// NPM
import React, { useEffect, Fragment } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';

import InvoiceShowFormattedMessages from '../../../../messages/billing/InvoiceShowFormattedMessages';

import { invoiceTableHeader } from '../../../../styles/tables';

const InvoiceDetails = ({
  invoiceDetailsData,
  invoice,
  startDate,
  endDate,
  getDataFunction,
}) => {
  useEffect(() => {
    getDataFunction();
  }, [getDataFunction]);

  const generateAdjustmentChargeTable = () => {
    const { bill_adjustments } = invoice;

    let charges = [];

    if (bill_adjustments) {
      bill_adjustments.forEach((element) => {
        charges.push({
          revenue: element.revenue,
          rate: element.rate,
          translation: element.translation + ' - ' + element.bill_desc,
        });
      });
    }

    let chargeRows = charges.map((element, index) => {
      return (
        <TableRow key={index}>
          <TableCell align="center">
            <Typography>{element.translation}</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography>{element.rate}</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography>{element.revenue}</Typography>
          </TableCell>
        </TableRow>
      );
    });

    if (chargeRows.length === 0)
      chargeRows = (
        <TableRow>
          <TableCell align="center" colSpan={3}>
            <Typography>This bill does not have any charges.</Typography>
          </TableCell>
        </TableRow>
      );

    return (
      <TableContainer>
        <Table
          width="100%"
          className="invoice-details"
          aria-label="adjustment charges table"
        >
          <TableBody>
            <TableRow>
              <TableCell colSpan={4} align="center" sx={invoiceTableHeader}>
                <Typography fontWeight={700}>Charges</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography align="center" fontWeight={700}>
                  Charge Description
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={700}>Rate</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={700}>
                  Revenue ({invoice.currency_type})
                </Typography>
              </TableCell>
            </TableRow>
            {chargeRows}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const generateCostCentreTable = () => {
    if (!invoiceDetailsData || !invoiceDetailsData.costCentres) return;

    let data = [];
    for (let i = 0; i < invoiceDetailsData.costCentres.length; i++) {
      let costCentre = invoiceDetailsData.costCentres[i];
      data.push(
        <TableRow key={i}>
          <TableCell colSpan="4" align="center">
            <Typography>{costCentre.cost_centre_name}</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography>{costCentre.cost_centre_quantity}</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography>{costCentre.cost_centre_revenue}</Typography>
          </TableCell>
        </TableRow>
      );
    }
    return (
      <TableContainer>
        <Table
          width="100%"
          className="invoice-details"
          aria-label="cost centre charges summary table"
        >
          <TableBody>
            <TableRow>
              <TableCell colSpan={7} align="center" sx={invoiceTableHeader}>
                <Typography fontWeight={700}>
                  {InvoiceShowFormattedMessages.costCenterChargesSummary}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan="4" align="center">
                <Typography fontWeight={700}>
                  {InvoiceShowFormattedMessages.costCenter}1
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={700}>
                  {InvoiceShowFormattedMessages.messages}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={700}>
                  {invoice.currency_type}
                </Typography>
              </TableCell>
            </TableRow>
            {data}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const generateLicenseTable = () => {
    if (!invoiceDetailsData.licenses) return;

    let licenseRows = invoiceDetailsData.licenses.map((license, index) => {
      return (
        <TableRow key={index}>
          <TableCell align="center">
            <Typography>{license.quantity}</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography>{license.type}</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography>{license.revenue}</Typography>
          </TableCell>
        </TableRow>
      );
    });

    if (licenseRows.length === 0)
      licenseRows = (
        <TableRow>
          <TableCell align="center" colSpan={3}>
            <Typography>This bill does not have any license fees.</Typography>
          </TableCell>
        </TableRow>
      );

    return (
      <TableContainer>
        <Table
          width="100%"
          className="invoice-details"
          aria-label="license fees table"
        >
          <TableBody>
            <TableRow>
              <TableCell colSpan={4} sx={invoiceTableHeader}>
                <Typography fontWeight={700}>
                  {InvoiceShowFormattedMessages.licenseFees}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography>
                  <b>No. Licenses</b>
                </Typography>
              </TableCell>
              <TableCell>
                <Typography>
                  <b>Type</b>
                </Typography>
              </TableCell>
              <TableCell>
                <Typography>
                  <b>Revenue ({invoice.currency_type})</b>
                </Typography>
              </TableCell>
            </TableRow>
            {licenseRows}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const generateUsersForCostCentreTable = () => {
    const rows = [];
    invoiceDetailsData?.costCentres?.forEach((costCentre, index) => {
      const rowSpan = costCentre?.cost_centre_users?.length || 1;

      costCentre?.cost_centre_users?.forEach((user, userIndex) => {
        rows.push(
          <TableRow key={`${index}-${userIndex}`}>
            {userIndex === 0 && (
              <TableCell align="center" rowSpan={rowSpan}>
                <Typography>{costCentre.cost_centre_name}</Typography>
              </TableCell>
            )}
            <TableCell align="center" colSpan="4">
              <Typography>{user.customer_user_name}</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>{user.quantity}</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>{user.revenue}</Typography>
            </TableCell>
          </TableRow>
        );
      });
    });

    if (!rows.length)
      rows.push(
        <TableRow>
          <TableCell align="center" colSpan={14}>
            <Typography>No entries found for billing period.</Typography>
          </TableCell>
        </TableRow>
      );

    return (
      <TableContainer>
        <Table
          width="100%"
          className="invoice-details"
          aria-label="users for cost centre table"
        >
          <TableBody>
            <TableRow>
              <TableCell colSpan={8} align="center" sx={invoiceTableHeader}>
                <Typography fontWeight={700}>
                  {
                    InvoiceShowFormattedMessages.chargeBreakdownbyUserForCostCenter
                  }
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="center">
                <Typography fontWeight={700}>Cost Centre</Typography>
              </TableCell>
              <TableCell colSpan="4" align="center">
                <Typography fontWeight={700}>User&apos;s Name</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={700}>Messages</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={700}>
                  {invoice.currency_type}
                </Typography>
              </TableCell>
            </TableRow>
            {rows}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (!invoiceDetailsData || !invoice) {
    return (
      <Fragment>
        <Typography>{InvoiceShowFormattedMessages.loading}</Typography>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Typography variant="h1" color="common.black">
        {InvoiceShowFormattedMessages.invoiceDetails}
      </Typography>
      <Typography component="h2" sx={{ color: 'common.black', fontSize: 18 }}>
        {InvoiceShowFormattedMessages.invoiceAnalysisFor}
        {`: ${invoice.customerName}`}
      </Typography>
      <br />
      <Typography fontWeight={700} gutterBottom>
        {InvoiceShowFormattedMessages.licenseAndMessageCharges}
        {` ${startDate} `} {InvoiceShowFormattedMessages.to} {` ${endDate}`}
      </Typography>
      {generateLicenseTable()}
      <br />
      {generateAdjustmentChargeTable()}
      <br />
      {generateCostCentreTable()}
      <br />
      {generateUsersForCostCentreTable()}
    </Fragment>
  );
};

export default InvoiceDetails;
